import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Collapse,
  Divider,
  Grid,
  Chip,
  Tooltip,
  Fade,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';
import { SAPTextField } from './SAPTextField';
import { SAPAutocomplete, SAPAutocompleteOption } from './SAPAutocomplete';
import { SAPSelect, SAPSelectOption } from './SAPSelect';

// Filter field interface
export interface SAPFilterField {
  id: string;
  label: string;
  type: 'text' | 'autocomplete' | 'select' | 'date' | 'number' | 'boolean';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  options?: SAPAutocompleteOption[] | SAPSelectOption[];
  validation?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    custom?: (value: any) => string | null;
  };
  config?: {
    multiple?: boolean;
    chipDisplay?: boolean;
    maxChips?: number;
    showOptionDescription?: boolean;
    showOptionIcon?: boolean;
    showOptionCode?: boolean;
    showOptionStatus?: boolean;
    groupBy?: (option: any) => string;
    filterOptions?: (options: any[], params: any) => any[];
  };
}

// Filter value interface
export interface SAPFilterValue {
  [key: string]: any;
}

// Component variants using CVA
const filterBarVariants = cva(
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        card: 'bg-white border border-gray-200 rounded-lg shadow-sm',
        paper: 'bg-white border border-gray-200 rounded-lg',
        minimal: 'bg-transparent border-none',
      },
      size: {
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
      },
      layout: {
        grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
        flex: 'flex flex-wrap gap-4',
        stack: 'flex flex-col gap-4',
      },
    },
    defaultVariants: {
      variant: 'card',
      size: 'md',
      layout: 'grid',
    },
  }
);

// Enhanced TypeScript interface
export interface SAPFilterBarProps {
  // Basic props
  title?: string;
  subtitle?: string;
  variant?: 'card' | 'paper' | 'minimal';
  size?: 'sm' | 'md' | 'lg';
  layout?: 'grid' | 'flex' | 'stack';
  
  // Filter configuration
  fields: SAPFilterField[];
  values: SAPFilterValue;
  onValuesChange: (values: SAPFilterValue) => void;
  
  // Actions
  onSearch?: (values: SAPFilterValue) => void;
  onReset?: () => void;
  onClear?: () => void;
  
  // State
  loading?: boolean;
  disabled?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  
  // Display options
  showSearchButton?: boolean;
  showResetButton?: boolean;
  showClearButton?: boolean;
  showActiveFilters?: boolean;
  showFilterCount?: boolean;
  
  // Customization
  searchButtonText?: string;
  resetButtonText?: string;
  clearButtonText?: string;
  noFiltersText?: string;
  
  // Styling
  customStyles?: React.CSSProperties;
  fieldStyles?: React.CSSProperties;
  
  // Callbacks
  onValidationChange?: (isValid: boolean, errors: Record<string, string[]>) => void;
  onFieldChange?: (fieldId: string, value: any) => void;
  
  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

// Styled components
const StyledFilterCard = styled(Card, {
  shouldForwardProp: (prop) => 
    !['variant', 'size', 'layout', 'customStyles'].includes(prop as string),
})<SAPFilterBarProps>(({ theme, variant = 'card', size = 'md', layout = 'grid', customStyles }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: SAP_SPACING.component.borderRadius,
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: variant === 'card' ? SAP_SHADOWS.sm : 'none',
  transition: SAP_TRANSITIONS.normal,
  
  '&:hover': {
    boxShadow: variant === 'card' ? SAP_SHADOWS.md : 'none',
  },
  
  ...customStyles,
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${SAP_SPACING.md} ${SAP_SPACING.lg}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
  
  '& .MuiTypography-root': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    fontSize: SAP_TYPOGRAPHY.fontSize.lg,
  },
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: SAP_SPACING.lg,
}));

const FilterActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: SAP_SPACING.sm,
  padding: `${SAP_SPACING.md} ${SAP_SPACING.lg}`,
  borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
  backgroundColor: SAP_COLORS.sapGray1,
}));

const ActiveFiltersChip = styled(Chip)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapBlue,
  border: `1px solid ${SAP_COLORS.sapBlue}`,
  borderRadius: SAP_SPACING.component.borderRadius,
  fontSize: SAP_TYPOGRAPHY.fontSize.sm,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  
  '& .MuiChip-deleteIcon': {
    color: SAP_COLORS.sapBlue,
    '&:hover': {
      color: SAP_COLORS.sapRed,
    },
  },
}));

// Main component
export const SAPFilterBar = forwardRef<HTMLDivElement, SAPFilterBarProps>(
  (
    {
      title = 'Search Filters',
      subtitle,
      variant = 'card',
      size = 'md',
      layout = 'grid',
      fields = [],
      values = {},
      onValuesChange,
      onSearch,
      onReset,
      onClear,
      loading = false,
      disabled = false,
      collapsible = false,
      defaultExpanded = true,
      showSearchButton = true,
      showResetButton = true,
      showClearButton = false,
      showActiveFilters = true,
      showFilterCount = true,
      searchButtonText = 'Search',
      resetButtonText = 'Reset',
      clearButtonText = 'Clear All',
      noFiltersText = 'No active filters',
      customStyles,
      fieldStyles,
      onValidationChange,
      onFieldChange,
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    // Local state
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    // Memoized active filters
    const activeFilters = useMemo(() => {
      return Object.entries(values).filter(([key, value]) => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });
    }, [values]);

    // Memoized filter count
    const filterCount = useMemo(() => activeFilters.length, [activeFilters]);

    // Enhanced field change handler
    const handleFieldChange = useCallback((fieldId: string, value: any) => {
      const newValues = { ...values, [fieldId]: value };
      onValuesChange(newValues);
      onFieldChange?.(fieldId, value);
      
      // Validate field
      validateField(fieldId, value);
    }, [values, onValuesChange, onFieldChange]);

    // Field validation
    const validateField = useCallback((fieldId: string, value: any) => {
      const field = fields.find(f => f.id === fieldId);
      if (!field?.validation) return;

      const errors: string[] = [];
      const { validation } = field;

      // Required validation
      if (validation.required && (!value || (Array.isArray(value) && value.length === 0))) {
        errors.push('This field is required');
      }

      // Length validation
      if (value && typeof value === 'string') {
        if (validation.minLength && value.length < validation.minLength) {
          errors.push(`Minimum ${validation.minLength} characters required`);
        }
        if (validation.maxLength && value.length > validation.maxLength) {
          errors.push(`Maximum ${validation.maxLength} characters allowed`);
        }
      }

      // Pattern validation
      if (validation.pattern && value && typeof value === 'string') {
        if (!validation.pattern.test(value)) {
          errors.push('Invalid format');
        }
      }

      // Custom validation
      if (validation.custom) {
        const customError = validation.custom(value);
        if (customError) errors.push(customError);
      }

      // Update validation errors
      setValidationErrors(prev => ({
        ...prev,
        [fieldId]: errors,
      }));
    }, [fields]);

    // Remove filter
    const handleRemoveFilter = useCallback((fieldId: string) => {
      const newValues = { ...values };
      delete newValues[fieldId];
      onValuesChange(newValues);
      
      // Clear validation error
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }, [values, onValuesChange]);

    // Handle search
    const handleSearch = useCallback(() => {
      if (onSearch) {
        onSearch(values);
      }
    }, [onSearch, values]);

    // Handle reset
    const handleReset = useCallback(() => {
      const resetValues: SAPFilterValue = {};
      fields.forEach(field => {
        resetValues[field.id] = field.type === 'boolean' ? false : '';
      });
      onValuesChange(resetValues);
      setValidationErrors({});
      onReset?.();
    }, [fields, onValuesChange, onReset]);

    // Handle clear
    const handleClear = useCallback(() => {
      onValuesChange({});
      setValidationErrors({});
      onClear?.();
    }, [onValuesChange, onClear]);

    // Render field based on type
    const renderField = useCallback((field: SAPFilterField) => {
      const value = values[field.id];
      const errors = validationErrors[field.id] || [];
      const hasError = errors.length > 0;

      const commonProps = {
        key: field.id,
        label: field.label,
        placeholder: field.placeholder,
        required: field.required,
        disabled: field.disabled || disabled,
        loading: field.loading || loading,
        error: hasError,
        helperText: errors[0],
        helperTextType: hasError ? 'error' as const : 'info' as const,
        fullWidth: true,
        size: 'sm' as const,
        sx: fieldStyles,
      };

      switch (field.type) {
        case 'text':
        case 'number':
          return (
            <SAPTextField
              {...commonProps}
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              showCharacterCount={field.validation?.maxLength ? true : false}
              maxLength={field.validation?.maxLength}
            />
          );

        case 'autocomplete':
          return (
            <SAPAutocomplete
              {...commonProps}
              options={field.options || []}
              value={value}
              onChange={(event, option) => handleFieldChange(field.id, option)}
              multiple={field.config?.multiple}
              showOptionDescription={field.config?.showOptionDescription}
              showOptionIcon={field.config?.showOptionIcon}
              showOptionCode={field.config?.showOptionCode}
              showOptionStatus={field.config?.showOptionStatus}
              groupBy={field.config?.groupBy}
              filterOptions={field.config?.filterOptions}
            />
          );

        case 'select':
          return (
            <SAPSelect
              {...commonProps}
              options={field.options || []}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              multiple={field.config?.multiple}
              chipDisplay={field.config?.chipDisplay}
              maxChips={field.config?.maxChips}
              showOptionDescription={field.config?.showOptionDescription}
              showOptionIcon={field.config?.showOptionIcon}
              showOptionCode={field.config?.showOptionCode}
              showOptionStatus={field.config?.showOptionStatus}
              groupBy={field.config?.groupBy}
            />
          );

        case 'date':
          return (
            <SAPTextField
              {...commonProps}
              type="date"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          );

        case 'boolean':
          return (
            <SAPSelect
              {...commonProps}
              options={[
                { label: 'Yes', value: 1 },
                { label: 'No', value: 0 },
              ]}
              value={value === undefined ? '' : value ? 1 : 0}
              onChange={(e) => handleFieldChange(field.id, String(e.target.value) === '1')}
            />
          );

        default:
          return null;
      }
    }, [values, validationErrors, disabled, loading, fieldStyles, handleFieldChange]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': ariaLabel || 'Filter bar',
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': collapsible ? expanded : undefined,
    };

    return (
      <StyledFilterCard
        ref={ref}
        variant={variant as any}
        size={size}
        layout={layout}
        customStyles={customStyles}
        {...(accessibilityProps as any)}
      >
        {/* Header */}
        <FilterHeader>
          <Box>
            <Typography variant="h6" sx={{ 
              color: SAP_COLORS.sapGray7, 
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ 
                color: SAP_COLORS.sapGray5,
                marginTop: SAP_SPACING.xs,
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showFilterCount && filterCount > 0 && (
              <Chip
                label={`${filterCount} active`}
                size="small"
                sx={{
                  backgroundColor: SAP_COLORS.sapLightGreen,
                  color: SAP_COLORS.sapGreen,
                  fontSize: SAP_TYPOGRAPHY.fontSize.xs,
                }}
              />
            )}
            
            {collapsible && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ color: SAP_COLORS.sapGray5 }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        </FilterHeader>

        {/* Content */}
        <Collapse in={!collapsible || expanded}>
          <FilterContent>
            <Box
              sx={{
                display: layout === 'grid' ? 'grid' : 'flex',
                gridTemplateColumns: layout === 'grid' ? {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(4, 1fr)',
                } : undefined,
                flexDirection: layout === 'stack' ? 'column' : 'row',
                flexWrap: layout === 'flex' ? 'wrap' : 'nowrap',
                gap: SAP_SPACING.md,
                alignItems: layout === 'stack' ? 'stretch' : 'end',
              }}
            >
              {fields.map(renderField)}
            </Box>

            {/* Active Filters */}
            {showActiveFilters && activeFilters.length > 0 && (
              <Box sx={{ marginTop: SAP_SPACING.lg }}>
                <Typography variant="subtitle2" sx={{ 
                  color: SAP_COLORS.sapGray6,
                  marginBottom: SAP_SPACING.sm,
                }}>
                  Active Filters:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {activeFilters.map(([fieldId, value]) => {
                    const field = fields.find(f => f.id === fieldId);
                    const displayValue = Array.isArray(value) 
                      ? value.map(v => field?.options?.find(opt => opt.value === v)?.label || v).join(', ')
                      : field?.options?.find(opt => opt.value === value)?.label || value;
                    
                    return (
                      <ActiveFiltersChip
                        key={fieldId}
                        label={`${field?.label}: ${displayValue}`}
                        onDelete={() => handleRemoveFilter(fieldId)}
                        size="small"
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </FilterContent>

          {/* Actions */}
          <FilterActions>
            <Box sx={{ flex: 1 }}>
              {activeFilters.length === 0 && (
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray5 }}>
                  {noFiltersText}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: SAP_SPACING.sm }}>
              {showClearButton && activeFilters.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClear}
                  disabled={disabled || loading}
                  startIcon={<ClearIcon />}
                  sx={{
                    color: SAP_COLORS.sapGray6,
                    borderColor: SAP_COLORS.sapGray3,
                    '&:hover': {
                      borderColor: SAP_COLORS.sapRed,
                      color: SAP_COLORS.sapRed,
                    },
                  }}
                >
                  {clearButtonText}
                </Button>
              )}
              
              {showResetButton && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  disabled={disabled || loading}
                  startIcon={<RefreshIcon />}
                  sx={{
                    color: SAP_COLORS.sapGray6,
                    borderColor: SAP_COLORS.sapGray3,
                    '&:hover': {
                      borderColor: SAP_COLORS.sapOrange,
                      color: SAP_COLORS.sapOrange,
                    },
                  }}
                >
                  {resetButtonText}
                </Button>
              )}
              
              {showSearchButton && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSearch}
                  disabled={disabled || loading}
                  startIcon={<SearchIcon />}
                  sx={{
                    backgroundColor: SAP_COLORS.sapBlue,
                    color: SAP_COLORS.sapWhite,
                    '&:hover': {
                      backgroundColor: SAP_COLORS.sapDarkBlue,
                    },
                    '&:disabled': {
                      backgroundColor: SAP_COLORS.sapGray3,
                      color: SAP_COLORS.sapGray4,
                    },
                  }}
                >
                  {searchButtonText}
                </Button>
              )}
            </Box>
          </FilterActions>
        </Collapse>
      </StyledFilterCard>
    );
  }
);

// Display name for debugging
SAPFilterBar.displayName = 'SAPFilterBar';

// Export types for external use
export { filterBarVariants };
